import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import { Navigation, Pagination, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { getStrapiMedia } from "../../utils/utils";
import "./HomepageProducts.scss";
import { CategoryComponent_Plain } from "../../types/components/products/CategoryComponent.ts";
import { ProductComponent_Plain } from "../../types/components/products/ProductComponent.ts";
import { useTranslation } from "react-i18next";

const HomepageProducts = ({
  data,
  title,
}: {
  data: CategoryComponent_Plain[];
  title: string;
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  // Keep SSR and the very first client render consistent to avoid hydration mismatch.
  // Real window width is applied after mount in `useEffect`.
  const [windowWidth, setWindowWidth] = useState(1024);
  const [openedItems, setOpenedItems] = useState<number[]>([]);
  const swiperRef = useRef<{ swiper: SwiperInstance } | null>(null);
  const tabsSwiperRef = useRef<{ swiper: SwiperInstance } | null>(null);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  let lastDirection: "next" | "prev" | null = null;
  const isTabSwitchingRef = useRef(false);

  useEffect(() => {
    // Update window width on mount (client-side only)
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
    }

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const categoryTitles = data.map((category, index) => ({
    title: category.title,
    id: category.id,
    key: index,
  }));
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setOpenedItems([]);
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(0);
    }
    /*if (tabsSwiperRef.current) {
      tabsSwiperRef.current.swiper.slideTo(index);
    }*/
  };

  const toggleOpenedItem = (index: number) => {
    setOpenedItems((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    );
  };

  const handleProductSlideChange = (swiper: SwiperInstance) => {
    setIsLastSlide(swiper.isEnd);
    setIsFirstSlide(swiper.isBeginning);
  };

  const handleTouchMove = (swiper: SwiperInstance) => {
    if (isTabSwitchingRef.current) return;

    lastDirection = swiper.swipeDirection;

    const slidesPerView = windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 1 : 1;
    const totalSlides = data[activeTab]?.product?.length || 0;
    const isAllVisible = totalSlides <= slidesPerView;

    const goToTab = (newTabIndex: number) => {
      setActiveTab(newTabIndex);
      isTabSwitchingRef.current = true;

      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.swiper.slideTo(0);
        }
        isTabSwitchingRef.current = false;
      }, 150);
    };

    if (!isAllVisible) {
      if (
        isLastSlide &&
        lastDirection === "next" &&
        activeTab < categoryTitles.length - 1
      ) {
        goToTab(activeTab + 1);
      } else if (isFirstSlide && lastDirection === "prev" && activeTab > 0) {
        goToTab(activeTab - 1);
      }
    } else {
      if (lastDirection === "next" && activeTab < categoryTitles.length - 1) {
        goToTab(activeTab + 1);
      } else if (lastDirection === "prev" && activeTab > 0) {
        goToTab(activeTab - 1);
      }
    }
  };

  return (
    <section className="box-products" id="products">
      <div className="wrapper">
        <div className="block-title">{title}</div>

        <div className="tabs">
          <Swiper
            ref={tabsSwiperRef}
            modules={[FreeMode, Navigation]}
            freeMode={true}
            slidesPerView="auto"
            spaceBetween={10}
            touchRatio={1}
            navigation
            className="tabs-nav"
          >
            {categoryTitles.map((category, index) => (
              <SwiperSlide key={category.key} style={{ width: "auto" }}>
                <div
                  className={`tabs-nav__item ${activeTab === index ? "active" : ""}`}
                  onClick={() => handleTabClick(index)}
                  data-index={index}
                >
                  <a href={`#${category.id}`}>{category.title}</a>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="tabs-content">
            <div className="product-list">
              {windowWidth <= 767 ? (
                data[activeTab]?.product.map(
                  (productItem: ProductComponent_Plain, productKey: number) => (
                    <div
                      key={productKey}
                      className={`product-list__item ${openedItems.includes(productKey) ? "opened-inf" : ""}`}
                    >
                      <div className="product-list__top">
                        <div className="product-list__num">
                          {`${(productKey + 1).toString().padStart(2, "0")}/${
                            data[activeTab]?.product.length || 1
                          }`}
                        </div>
                        {productItem.icon ? (
                          <div className="product-list__icon">
                            <img
                              src={getStrapiMedia(productItem.icon.url)}
                              width="34"
                              height="34"
                              alt={productItem.title}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ) : null}
                      </div>
                      <div className="product-list__text">
                        <h2>{productItem.title}</h2>
                        <p
                          dangerouslySetInnerHTML={{
                            __html: productItem.description,
                          }}
                        ></p>
                        <a
                          href="#"
                          className="read-more"
                          onClick={(e) => {
                            e.preventDefault();
                            toggleOpenedItem(productKey);
                          }}
                        >
                          {t("Read more")}{" "}
                          <span className="read-more__icon"></span>
                        </a>
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="product-swiper-container">
                  <Swiper
                    ref={swiperRef}
                    modules={[Navigation, Pagination]}
                    spaceBetween={16}
                    slidesPerView={3}
                    breakpoints={{
                      768: { slidesPerView: 1 },
                      1024: { slidesPerView: 3 },
                    }}
                    navigation
                    pagination={{
                      clickable: true,
                      type: "fraction",
                    }}
                    touchRatio={1}
                    onSlideChange={handleProductSlideChange}
                    onTouchMove={handleTouchMove}
                    className="product-swiper"
                  >
                    {data[activeTab]?.product.map(
                      (
                        productItem: ProductComponent_Plain,
                        productKey: number,
                      ) => (
                        <SwiperSlide key={productKey}>
                          <div
                            className={`product-list__item ${openedItems.includes(productKey) ? "opened-inf" : ""}`}
                          >
                            <div className="product-list__top">
                              <div className="product-list__num">
                                {`${(productKey + 1).toString().padStart(2, "0")}/${
                                  data[activeTab]?.product.length || 1
                                }`}
                              </div>
                              {productItem.icon ? (
                                <div className="product-list__icon">
                                  <img
                                    src={getStrapiMedia(productItem.icon.url)}
                                    width="34"
                                    height="34"
                                    alt={productItem.title}
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              ) : null}
                            </div>
                            <div className="product-list__text">
                              <h2>{productItem.title}</h2>
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: productItem.description,
                                }}
                              ></p>
                              <a
                                href="#"
                                className="read-more"
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleOpenedItem(productKey);
                                }}
                              >
                                {t("Read more")}{" "}
                                <span className="read-more__icon"></span>
                              </a>
                            </div>
                          </div>
                        </SwiperSlide>
                      ),
                    )}
                  </Swiper>
                </div>
              )}
            </div>
            <div className="box-link-download">
              {data[activeTab]?.detailPage ? (
                <div className="link-download">
                  <a className="btn-border" href={data[activeTab].detailPage}>
                    {t("Detail")}
                  </a>
                </div>
              ) : (
                ""
              )}
              {data[activeTab]?.link?.title?.trim() ? (
                <div className="link-download">
                  <a
                    className="btn-border btn-border_download"
                    href={data[activeTab].link.link}
                    target={data[activeTab].link.target}
                  >
                    {data[activeTab].link.title}
                  </a>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomepageProducts;
